import type { SchedulerRefreshReason } from './schedulerRefreshController';

export type SchedulerRefreshScopeKeyInput = {
  unitIds: string[];
  userId?: string | null;
};

export type SchedulerBootstrapRefreshDedupeState = {
  inFlightScopeKey: string | null;
  completedScopeKey: string | null;
  firstPaintCompleted: boolean;
};

export type SchedulerBootstrapRefreshDedupeDecision =
  | { action: 'allow'; reason: 'first-bootstrap-refresh' | 'different-scope' | 'after-first-paint' | 'manual-refresh' | 'soft-refresh' }
  | { action: 'dedupe'; reason: 'same-bootstrap-scope-in-flight' | 'same-bootstrap-scope-completed' };

const BOOTSTRAP_SCOPE_DEDUPE_REASONS = new Set<SchedulerRefreshReason>([
  'app_open',
  'unit_change',
  // The current `/api/shifts` bootstrap read is unit-scoped, not week-scoped, so a
  // pre-first-paint week_change for the same unit/user scope is redundant for the
  // canonical shift payload. If the API becomes week-scoped later, add a week key
  // to buildSchedulerRefreshScopeKey before relying on this dedupe.
  'week_change',
]);



export type SchedulerAppOpenBootstrapState = {
  scopeKey: string | null;
  inFlight: boolean;
  completed: boolean;
  firstPaintCompleted: boolean;
};

export type SchedulerAppOpenIdempotencyDecision =
  | { action: 'allow'; reason: 'first-app-open' | 'changed-scope' | 'after-first-paint' }
  | { action: 'dedupe'; reason: 'same-app-open-scope-in-flight' | 'same-app-open-scope-completed' };

export const createInitialSchedulerAppOpenBootstrapState = (): SchedulerAppOpenBootstrapState => ({
  scopeKey: null,
  inFlight: false,
  completed: false,
  firstPaintCompleted: false,
});

export const resolveSchedulerAppOpenIdempotency = ({
  scopeKey,
  state,
}: {
  scopeKey: string;
  state: SchedulerAppOpenBootstrapState;
}): SchedulerAppOpenIdempotencyDecision => {
  if (state.firstPaintCompleted) return { action: 'allow', reason: 'after-first-paint' };
  if (state.scopeKey && state.scopeKey !== scopeKey) return { action: 'allow', reason: 'changed-scope' };
  if (state.scopeKey === scopeKey && state.inFlight) return { action: 'dedupe', reason: 'same-app-open-scope-in-flight' };
  if (state.scopeKey === scopeKey && state.completed) return { action: 'dedupe', reason: 'same-app-open-scope-completed' };
  return { action: 'allow', reason: 'first-app-open' };
};



export const resolveSchedulerAppOpenLifecycleCancelled = ({
  scopeKey,
  state,
}: {
  scopeKey: string;
  state: SchedulerAppOpenBootstrapState;
}): SchedulerAppOpenBootstrapState => {
  if (state.scopeKey !== scopeKey || !state.inFlight) return state;
  return {
    ...state,
    inFlight: false,
    completed: false,
  };
};

export const buildSchedulerRefreshScopeKey = ({ unitIds, userId }: SchedulerRefreshScopeKeyInput): string => {
  const normalizedUnitIds = Array.from(
    new Set(
      unitIds
        .map(unitId => (typeof unitId === 'string' ? unitId.trim() : ''))
        .filter(Boolean)
    )
  ).sort();

  return JSON.stringify({
    units: normalizedUnitIds,
    user: userId || 'anonymous',
  });
};

export const resolveSchedulerBootstrapRefreshDedupe = ({
  reason,
  scopeKey,
  state,
}: {
  reason: SchedulerRefreshReason;
  scopeKey: string;
  state: SchedulerBootstrapRefreshDedupeState;
}): SchedulerBootstrapRefreshDedupeDecision => {
  if (reason === 'manual') return { action: 'allow', reason: 'manual-refresh' };
  if (!BOOTSTRAP_SCOPE_DEDUPE_REASONS.has(reason)) return { action: 'allow', reason: 'soft-refresh' };
  if (state.firstPaintCompleted) return { action: 'allow', reason: 'after-first-paint' };
  if (state.inFlightScopeKey === scopeKey) return { action: 'dedupe', reason: 'same-bootstrap-scope-in-flight' };
  if (state.completedScopeKey === scopeKey) return { action: 'dedupe', reason: 'same-bootstrap-scope-completed' };
  if (state.inFlightScopeKey || state.completedScopeKey) return { action: 'allow', reason: 'different-scope' };
  return { action: 'allow', reason: 'first-bootstrap-refresh' };
};
